import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { IMG, FLAGSHIP } from "@/lib/data";
import { HOME } from "@/constants/testIds";
import { RevealLines, RevealWords } from "@/components/RevealText";
import { Reveal, StaggerContainer, StaggerItem } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";

/* -------------------- HERO -------------------- */
function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.42, 0.75]);

  return (
    <section
      ref={ref}
      data-testid={HOME.hero}
      className="relative min-h-[100svh] w-full overflow-hidden bg-obsidian text-bone"
    >
      {/* Parallax background image */}
      <motion.div
        style={{ y, scale }}
        className="absolute inset-0 will-change-transform"
      >
        <img
          src={IMG.heroHerdSunset}
          alt="Black Angus herd grazing at sunset beneath the Bitterroot Mountains"
          className="h-[115%] w-full object-cover object-[center_72%]"
          loading="eager"
          fetchPriority="high"
        />
      </motion.div>

      {/* scrim */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-obsidian/30 to-obsidian/80"
      />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      {/* Top row */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 pt-28 md:pt-36">
        <div className="flex items-center justify-between">
          <RevealWords
            text="Stevensville, Montana"
            className="font-mono text-[11px] tracking-overline uppercase text-bone/80"
            delay={0.2}
          />
          <RevealWords
            text="The Bitterroot Program"
            className="hidden sm:block font-mono text-[11px] tracking-overline uppercase text-bone/80"
            delay={0.35}
          />
        </div>
      </div>

      {/* Headline */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 pt-16 md:pt-24">
        <RevealLines
          as="h1"
          lines={["A quiet ranch", "beneath a", "loud sky."]}
          className="font-serif text-[16vw] md:text-[11vw] lg:text-[9.5vw] leading-[0.9] tracking-tightest text-bone"
          delay={0.4}
          stagger={0.14}
        />
      </div>

      {/* Sub row */}
      <div className="relative z-10 mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 pb-16 md:pb-24 mt-16 md:mt-24">
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6 md:col-start-7">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1], delay: 1.05 }}
              className="text-bone/85 text-base md:text-lg max-w-[46ch] leading-relaxed"
            >
              A private seedstock program raising Registered Black Angus and
              Charolais cattle in the Bitterroot Valley. Bulls and replacement
              heifers built on phenotype, sound feet, good udders and honest
              maternal value.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: 1.35 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/bulls"
                data-testid={HOME.heroCta}
                className="group inline-flex items-center gap-3 px-6 py-3.5 bg-bone text-ink text-[12px] uppercase tracking-overline hover:bg-terracotta hover:text-bone transition-colors"
              >
                Meet Confluence 4273
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <Link
                to="/herd"
                data-testid={HOME.heroSubCta}
                className="group inline-flex items-center gap-3 px-6 py-3.5 border border-bone/60 text-bone text-[12px] uppercase tracking-overline hover:bg-bone hover:text-ink transition-colors"
              >
                Our Herd
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Bottom-left ticker anchor */}
      <div className="absolute bottom-8 left-6 md:left-10 lg:left-14 z-10 flex items-center gap-6 text-bone/80 font-mono text-[10px] tracking-overline uppercase">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.4, duration: 0.6 }}
          className="flex items-center gap-2"
        >
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-terracotta animate-pulse" />
          Live from the ranch
        </motion.span>
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.55, duration: 0.6 }}
          className="hidden md:inline"
        >
          46.5° N · 114.1° W
        </motion.span>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6 }}
        className="absolute bottom-8 right-6 md:right-10 lg:right-14 z-10 flex flex-col items-end gap-2 text-bone/80 font-mono text-[10px] tracking-overline uppercase"
      >
        <span>Scroll</span>
        <motion.span
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
          className="inline-block h-6 w-px bg-bone/60"
        />
      </motion.div>
    </section>
  );
}

/* -------------------- CHAPTER MANIFESTO -------------------- */
const chapters = [
  {
    kicker: "The Whole Animal",
    title: "We breed for phenotype first.",
    body:
      "One quiet standard: the whole animal. Moderate, balanced, structurally correct cattle — chosen for sound feet, good udders and honest maternal value before anything else. No single extreme, no fads.",
    testId: HOME.chapter01,
  },
  {
    kicker: "The Land",
    title: "The Bitterroot teaches patience.",
    body:
      "Between the Sapphires and the Bitterroots, our pastures see hard sun and long winter. It's a country that rewards good feet, sound udders and honest cattle. We move slowly. We select ruthlessly.",
    testId: HOME.chapter02,
  },
  {
    kicker: "The Program",
    title: "Functional, problem-free genetics.",
    body:
      "Registered Angus and Charolais bulls and replacement heifers — selected for feet, legs, udders and maternal traits. Cattle bred to breed back, hold up, and improve a commercial cow herd's bottom line.",
    testId: HOME.chapter03,
  },
];

function Manifesto() {
  return (
    <section className="relative bg-bone text-ink noise-overlay-light">
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-32 md:py-40">
        <Reveal>
          <p className="font-mono text-[11px] tracking-overline uppercase text-sage">
            — A Manifesto
          </p>
          <h2 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-ink max-w-4xl">
            We are not in the cattle business.
            <br />
            <span className="italic text-sage">
              We are in the standard business.
            </span>
          </h2>
        </Reveal>

        <div className="mt-24 md:mt-32 space-y-24 md:space-y-32">
          {chapters.map((c) => (
            <StaggerContainer
              key={c.kicker}
              className="grid grid-cols-12 gap-6 md:gap-10"
            >
              <StaggerItem className="col-span-12 md:col-span-5">
                <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
                  {c.kicker}
                </p>
                <h3
                  data-testid={c.testId}
                  className="mt-4 font-serif text-3xl md:text-4xl lg:text-5xl leading-[1.02] tracking-tight text-ink"
                >
                  {c.title}
                </h3>
              </StaggerItem>
              <StaggerItem className="col-span-12 md:col-span-6 md:col-start-7">
                <p className="text-ink/80 leading-relaxed text-lg font-light max-w-[52ch]">
                  {c.body}
                </p>
                <div className="mt-6 h-px w-32 bg-terracotta" />
              </StaggerItem>
            </StaggerContainer>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- PARALLAX EDITORIAL -------------------- */
function ParallaxBand() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);

  return (
    <section
      ref={ref}
      className="relative h-[70svh] md:h-[95svh] w-full overflow-hidden bg-obsidian"
    >
      <motion.div style={{ y }} className="absolute inset-0 will-change-transform">
        <img
          src={IMG.goldenSun}
          alt="Golden sun cresting the Bitterroot Mountains over the hay field"
          className="h-[125%] w-full object-cover"
          loading="lazy"
        />
      </motion.div>
      <div className="absolute inset-0 bg-obsidian/30" />
      <div className="absolute inset-0 noise-overlay pointer-events-none" />

      <div className="relative z-10 h-full mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 flex items-end pb-16 md:pb-24">
        <div className="grid grid-cols-12 w-full gap-6">
          <Reveal className="col-span-12 md:col-span-6" y={40}>
            <p className="font-mono text-[11px] tracking-overline uppercase text-bone/80">
              — Interlude · Golden Hour
            </p>
            <p className="mt-4 font-serif text-4xl md:text-6xl leading-[1] tracking-tight text-bone">
              The light out here <br />
              <span className="italic">does the talking.</span>
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------- FLAGSHIP BULL SPOTLIGHT -------------------- */
function BullSpotlight() {
  return (
    <section className="relative bg-obsidian text-bone noise-overlay overflow-hidden">
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-28 md:py-40">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <div>
              <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
                — Flagship · Sire Spotlight
              </p>
              <h2 className="mt-4 font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-bone">
                {FLAGSHIP.name}
              </h2>
            </div>
            <div className="font-mono text-[11px] tracking-overline uppercase text-limestone">
              Reg # {FLAGSHIP.regNumber} · DOB {FLAGSHIP.dob}
            </div>
          </div>
        </Reveal>

        <div className="mt-16 md:mt-20 grid grid-cols-12 gap-6 md:gap-10 items-start">
          <Reveal className="col-span-12 md:col-span-7" y={40}>
            <div className="relative overflow-hidden border border-bone/10 bg-obsidian">
              <img
                src={IMG.flagshipBull}
                alt="VDAR Confluence 4273 — Registered Black Angus flagship sire"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <div className="absolute inset-0 pointer-events-none noise-overlay" />
            </div>
            <p className="mt-6 font-mono text-[11px] tracking-overline uppercase text-limestone">
              Sire: {FLAGSHIP.sire} · Dam: {FLAGSHIP.dam}
            </p>
          </Reveal>

          <div className="col-span-12 md:col-span-5">
            <Reveal y={40} delay={0.05}>
              <p className="text-bone/85 leading-relaxed text-lg font-light">
                {FLAGSHIP.commentary}
              </p>
            </Reveal>

            {/* EPD MINI TABLE */}
            <Reveal y={40} delay={0.1}>
              <div className="mt-10 border border-bone/15">
                <div className="grid grid-cols-4 divide-x divide-bone/10">
                  {FLAGSHIP.epds.slice(0, 8).map((e) => (
                    <div key={e.k} className="p-4 md:p-5">
                      <div className="font-mono text-[10px] tracking-overline uppercase text-limestone">
                        {e.k}
                      </div>
                      <div className="mt-2 font-mono text-2xl md:text-3xl text-bone tabular-nums">
                        {e.v}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Link
                to="/bulls"
                data-testid={HOME.bullSpotlightCta}
                className="mt-8 inline-flex items-center gap-3 px-6 py-3.5 border border-bone/70 text-bone text-[12px] uppercase tracking-overline hover:bg-bone hover:text-ink transition-colors"
              >
                View full pedigree & EPDs
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- HERD PREVIEW GRID -------------------- */
function HerdGrid() {
  const items = [
    {
      title: "Registered Black Angus",
      sub: "Maternal power. Marbling. Docility.",
      href: "/herd",
      img: IMG.angusHeifer,
      aspect: "clip-editorial-tall",
      pos: "object-[20%_center]",
    },
    {
      title: "Registered Charolais",
      sub: "Growth, muscle, gentle temperament.",
      href: "/herd",
      img: IMG.charolaisFlagship,
      aspect: "clip-editorial-tall",
      pos: "object-[18%_center]",
    },
    {
      title: "Seedstock",
      sub: "Registered females, structurally selected.",
      href: "/heifers",
      img: IMG.seedstockHerd,
      aspect: "clip-editorial-tall",
      pos: "object-center",
    },
  ];
  return (
    <section className="relative bg-parchment text-ink">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-28 md:py-36">
        <div className="grid grid-cols-12 gap-6 items-end">
          <Reveal className="col-span-12 md:col-span-6">
            <p className="font-mono text-[11px] tracking-overline uppercase text-sage">
              — The Herd
            </p>
            <h2 className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink">
              Purebreds
              <br />
              <span className="italic text-sage">& hybrid cross.</span>
            </h2>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-5 md:col-start-8" y={20}>
            <p className="text-ink/70 leading-relaxed max-w-[46ch]">
              The Angus contributes maternal power and carcass integrity. The
              Charolais contributes growth and gentle temperament. Together they
              produce cattle that work — in the pasture, in the ring and in the
              feedyard.
            </p>
          </Reveal>
        </div>

        <StaggerContainer className="mt-16 md:mt-24 grid grid-cols-12 gap-6 md:gap-8">
          {items.map((it, i) => (
            <StaggerItem
              key={it.title}
              className={`col-span-12 sm:col-span-6 lg:col-span-4 ${
                i === 1 ? "lg:translate-y-16" : ""
              }`}
            >
              <Link to={it.href} className="group block">
                <div
                  className={`relative overflow-hidden ${it.aspect} bg-ink/5`}
                >
                  <img
                    src={it.img}
                    alt={it.title}
                    loading="lazy"
                    className={`h-full w-full object-cover ${it.pos} transition-transform duration-[1200ms] ease-[cubic-bezier(0.19,1,0.22,1)] group-hover:scale-[1.06]`}
                  />
                  <div className="absolute inset-0 bg-obsidian/0 group-hover:bg-obsidian/10 transition-colors" />
                </div>
                <div className="mt-5 flex items-baseline justify-between gap-4">
                  <h3 className="font-serif text-2xl md:text-3xl text-ink tracking-tight">
                    {it.title}
                  </h3>
                  <span className="font-mono text-[11px] tracking-overline uppercase text-sage group-hover:text-terracotta transition-colors">
                    View →
                  </span>
                </div>
                <p className="mt-2 text-ink/70 text-sm">{it.sub}</p>
              </Link>
            </StaggerItem>
          ))}
        </StaggerContainer>
      </div>
    </section>
  );
}

/* -------------------- BRAND SEAL -------------------- */
function BrandSeal() {
  return (
    <section className="relative bg-obsidian text-bone noise-overlay overflow-hidden">
      <div className="relative mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <div className="grid grid-cols-12 gap-10 items-center">
          <Reveal className="col-span-12 md:col-span-5 flex md:justify-start justify-center">
            <motion.img
              src={IMG.logoBone}
              alt="Hideaway Cattle Co. — Stevensville, Montana"
              loading="lazy"
              initial={{ scale: 0.94, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="w-[260px] md:w-[340px] h-auto"
            />
          </Reveal>
          <Reveal className="col-span-12 md:col-span-6 md:col-start-7" y={30}>
            <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
              — The Mark
            </p>
            <h2 className="mt-5 font-serif text-4xl md:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-bone">
              Under the sign of <br />
              <span className="italic text-limestone">the key.</span>
            </h2>
            <p className="mt-6 text-limestone leading-relaxed max-w-[48ch]">
              A quiet place worth finding. The Hideaway mark stands for
              cattle raised with patience, bred with intention, and handled with
              a gentle hand — in the Bitterroot Valley of Stevensville, Montana.
            </p>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------- CTA CLOSER -------------------- */
function ClosingCTA() {
  return (
    <section className="relative bg-bone text-ink">
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-28 md:py-40">
        <div className="grid grid-cols-12 gap-6 items-end">
          <Reveal className="col-span-12 md:col-span-8">
            <p className="font-mono text-[11px] tracking-overline uppercase text-sage">
              — Correspondence
            </p>
            <h2 className="mt-6 font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-ink">
              Come find us
              <br />
              <span className="italic text-sage">on the Bitterroot.</span>
            </h2>
          </Reveal>
          <Reveal className="col-span-12 md:col-span-4" y={20}>
            <p className="text-ink/75 leading-relaxed max-w-[42ch]">
              Whether you&apos;re building a maternal herd or looking for a specific
              sire, we&apos;d like to hear from you.
            </p>
            <Link
              to="/contact"
              className="mt-8 inline-flex items-center gap-3 px-6 py-3.5 bg-ink text-bone text-[12px] uppercase tracking-overline hover:bg-terracotta transition-colors"
              data-testid="home-closing-cta"
            >
              Begin an inquiry
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="bg-bone">
      <Hero />
      <EditorialMarquee testId={HOME.marquee} />
      <Manifesto />
      <ParallaxBand />
      <BullSpotlight />
      <HerdGrid />
      <BrandSeal />
      <ClosingCTA />
    </div>
  );
}
